import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { OrderDetailsApiAdapter, OrderDetailsFilters } from '../adapters/OrderDetailsApiAdapter';
import { OrderDetailsService } from '../services/OrderDetailsService';

export const useOrderDetailsInfinite = (orderIds: number[], enabled: boolean = true) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new OrderDetailsApiAdapter(httpClient);
  const orderDetailsService = new OrderDetailsService(apiAdapter);

  const filters: OrderDetailsFilters = {
    ids: orderIds,
    per_page: 100, // Límite por página
  };

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfiniteQuery({
      queryKey: ['order-details-infinite', orderIds.sort().join(',')],
      queryFn: ({ pageParam = 1 }) =>
        orderDetailsService.getOrdersByIdsPaginated({
          ...filters,
          page: pageParam,
        }),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      enabled: enabled && orderIds.length > 0,
      staleTime: 2 * 60 * 1000, // 2 minutos
      gcTime: 5 * 60 * 1000, // 5 minutos
      placeholderData: (previousData) => previousData,
    });

  // 🎯 Aplanar todas las páginas en un array único
  const orders = data?.pages.flatMap((page) => page.orders) ?? [];

  // 🎯 Obtener el total del API (siempre será el mismo en todas las páginas)
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  return {
    orders,
    isLoading: isLoading || isPending,
    error,
    refetch,
    // 🎯 Props para scroll infinito
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    // 🎯 Stats útiles
    totalPages: data?.pages.length ?? 0,
    totalOrders: orders.length,
    total: totalFromApi, // 🎯 Total real del API
  };
};
